import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enCommon from '../locales/en/common.json';
import arCommon from '../locales/ar/common.json';

export const SUPPORTED_LOCALES = ['en', 'ar'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Locales that should flip the layout to right-to-left. */
const RTL_LOCALES: ReadonlySet<string> = new Set(['ar', 'he', 'fa', 'ur']);

export function isRtlLocale(lng: string | undefined): boolean {
  if (!lng) return false;
  // Match "ar", "ar-EG", etc. by checking the language subtag only.
  return RTL_LOCALES.has(lng.split('-')[0]!);
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    nonExplicitSupportedLngs: true,
    debug: import.meta.env.DEV,
    defaultNS: 'common',
    ns: ['common'],
    resources: {
      en: { common: enCommon },
      ar: { common: arCommon },
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'lang',
    },
    react: {
      useSuspense: false,
    },
  });

/**
 * Apply `dir`/`lang` attributes to `<html>` based on the active locale.
 * Wired to an i18next `languageChanged` listener in `main.tsx`.
 */
export function applyDocumentLang(lng: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lng;
  document.documentElement.dir = isRtlLocale(lng) ? 'rtl' : 'ltr';
}

export default i18n;
